import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./ExamPage.scss";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const ExamPage = () => {
  const [examData, setExamData] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [questionStatuses, setQuestionStatuses] = useState({});
  const [userAnswers, setUserAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState("questions");
  const [testData, setTestData] = useState(null);
  const [instructions, setInstructions] = useState("");
  const examId = localStorage.getItem("test_id");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username");
  const timerRef = useRef(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible); // Toggle sidebar visibility
  };

  const modelId = localStorage.getItem("test_id");
  useEffect(() => {
    fetchInstructions();
  }, []);

  const fetchInstructions = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/model-paper-instructions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // ✅ important!
          },
          body: JSON.stringify({
            id: modelId,
          }),
        }
      );

      const data = await res.json(); // ✅ parse JSON

      if (data.status === "1") {
        setInstructions(data.data.description || "");
        setTestData(data.data); // make sure testData is also set
      } else {
        setInstructions("No instructions available.");
      }
    } catch (error) {
      console.error("Error fetching instructions:", error);
      setInstructions("Error loading instructions.");
    }
  };

  // Load saved state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem(`exam_state_${examId}`);
    if (savedState) {
      const state = JSON.parse(savedState);

      if (state.startTime && state.timeLeft) {
        const timeElapsed = Math.floor((Date.now() - state.startTime) / 1000);
        const actualTimeLeft = Math.max(0, state.timeLeft - timeElapsed);
        setTimeLeft(actualTimeLeft);
      } else {
        setTimeLeft(state.timeLeft);
      }

      setUserAnswers(state.userAnswers || {});
      setQuestionStatuses(state.questionStatuses || {});
      setCurrentSectionIndex(state.currentSectionIndex || 0);
      setCurrentPassageIndex(state.currentPassageIndex || 0);
      setCurrentQuestionIndex(state.currentQuestionIndex || 0);
    }
  }, [examId]);

  // Save state to localStorage
  useEffect(() => {
    if (examId && examData && timeLeft !== null) {
      const stateToSave = {
        timeLeft,
        userAnswers,
        questionStatuses,
        currentSectionIndex,
        currentPassageIndex,
        currentQuestionIndex,
        examTitle: examData.title,
        startTime: Date.now(),
      };
      localStorage.setItem(`exam_state_${examId}`, JSON.stringify(stateToSave));
    }
  }, [
    timeLeft,
    userAnswers,
    questionStatuses,
    currentSectionIndex,
    currentPassageIndex,
    currentQuestionIndex,
    examId,
    examData,
  ]);

  useEffect(() => {
    fetchExamData();
  }, []);

  // Initialize timer when examData is loaded
  useEffect(() => {
    if (examData && timeLeft === null) {
      const savedState = localStorage.getItem(`exam_state_${examId}`);
      if (!savedState) {
        setTimeLeft(examData.duration_minutes * 60);
      }
    }
  }, [examData, timeLeft, examId]);

  // Timer effect
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) {
      if (timeLeft === 0) {
        handleAutoSubmit();
      }
      return;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeLeft]);

  const handleAutoSubmit = () => {
    Swal.fire({
      title: "Time Up!",
      text: "Your test has been automatically submitted.",
      icon: "warning",
      confirmButtonText: "OK",
    }).then(() => {
      handleSubmitTest();
    });
  };

  const fetchExamData = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/model-qusition-list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: examId }),
        }
      );
      const data = await res.json();

      if (data.status === "1" && data.data) {
        const exam = data.data;
        setExamData(exam);

        // Initialize question statuses and answers if not already loaded
        if (Object.keys(userAnswers).length === 0) {
          initializeQuestionStates(exam);
        }
      } else {
        setError("Exam data not available");
      }
    } catch (err) {
      console.error("Error fetching exam data:", err);
      setError("Failed to load exam. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const initializeQuestionStates = (exam) => {
    const allQuestions = getAllQuestions(exam);
    const initialStatuses = {};
    const initialAnswers = {};

    allQuestions.forEach((q, index) => {
      initialStatuses[index] = "not_answered";
      initialAnswers[index] = {
        type: q.question_type,
        answer: null,
      };
    });

    setQuestionStatuses(initialStatuses);
    setUserAnswers(initialAnswers);
  };

  // Get all questions flattened with proper indexing
  const getAllQuestions = (data = examData) => {
    if (!data?.sections) return [];

    const allQuestions = [];
    data.sections.forEach((section, sectionIndex) => {
      if (section.passages) {
        section.passages.forEach((passage, passageIndex) => {
          if (passage.questions) {
            passage.questions.forEach((question, questionIndex) => {
              const globalIndex = allQuestions.length;
              allQuestions.push({
                ...question,
                sectionIndex,
                passageIndex,
                questionIndex,
                sectionId: section.id,
                passageId: passage.id,
                globalIndex,
                sectionTitle: section.section_title,
                passageTitle: passage.passage_title,
              });
            });
          }
        });
      }
    });
    return allQuestions;
  };

  // Get current question object
  const getCurrentQuestion = () => {
    const allQuestions = getAllQuestions();
    return (
      allQuestions.find(
        (q) =>
          q.sectionIndex === currentSectionIndex &&
          q.passageIndex === currentPassageIndex &&
          q.questionIndex === currentQuestionIndex
      ) || null
    );
  };

  // Get questions for current section and passage
  const getCurrentQuestions = () => {
    if (
      !examData?.sections?.[currentSectionIndex]?.passages?.[
        currentPassageIndex
      ]?.questions
    ) {
      return [];
    }
    return examData.sections[currentSectionIndex].passages[currentPassageIndex]
      .questions;
  };

  // Get current global index
  const getCurrentGlobalIndex = () => {
    const allQuestions = getAllQuestions();
    const currentQuestion = getCurrentQuestion();
    return currentQuestion ? currentQuestion.globalIndex : 0;
  };

  const getTotalQuestions = () => {
    return getAllQuestions().length;
  };

  // Get questions for current section only (for sidebar)
  const getCurrentSectionQuestions = () => {
    if (!examData?.sections?.[currentSectionIndex]) return [];

    const sectionQuestions = [];
    examData.sections[currentSectionIndex].passages.forEach(
      (passage, pIndex) => {
        if (passage.questions) {
          passage.questions.forEach((question, qIndex) => {
            sectionQuestions.push({
              ...question,
              sectionIndex: currentSectionIndex,
              passageIndex: pIndex,
              questionIndex: qIndex,
              localIndex: sectionQuestions.length, // Local index within section
            });
          });
        }
      }
    );
    return sectionQuestions;
  };

  const handleSubmitTest = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Calculate results
    const totalQuestions = getTotalQuestions();
    const answeredQuestions = Object.values(userAnswers).filter(
      (answer) => answer.answer !== null && answer.answer !== ""
    ).length;

    const markedQuestions = Object.values(questionStatuses).filter(
      (status) => status === "marked"
    ).length;

    const notAnsweredQuestions = totalQuestions - answeredQuestions;

    // Prepare result data
    const resultData = {
      examData,
      userAnswers,
      questionStatuses,
      totalQuestions,
      answeredQuestions,
      markedQuestions,
      notAnsweredQuestions,
      timeTaken: examData.duration_minutes * 60 - timeLeft,
      allQuestions: getAllQuestions(),
    };

    // Save result data to localStorage for ResultPage
    localStorage.setItem(`exam_result_${examId}`, JSON.stringify(resultData));

    // Clear exam state
    localStorage.removeItem(`exam_state_${examId}`);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Navigate to result page
    navigate("/exam-result", {
      state: { resultData },
      replace: true,
    });

    setIsSubmitting(false);
  };

  const formatTime = (seconds) => {
    if (seconds === null) return "00:00:00";
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  const handleOptionChange = (option) => {
    const currentGlobalIndex = getCurrentGlobalIndex();

    const updatedAnswers = {
      ...userAnswers,
      [currentGlobalIndex]: {
        type: getCurrentQuestion().question_type,
        answer: option,
      },
    };
    setUserAnswers(updatedAnswers);

    setQuestionStatuses((prev) => ({
      ...prev,
      [currentGlobalIndex]: "answered",
    }));
  };

  const handleTextAnswerChange = (e) => {
    const answer = e.target.value;
    const currentGlobalIndex = getCurrentGlobalIndex();

    const updatedAnswers = {
      ...userAnswers,
      [currentGlobalIndex]: {
        type: getCurrentQuestion().question_type,
        answer: answer,
      },
    };
    setUserAnswers(updatedAnswers);

    setQuestionStatuses((prev) => ({
      ...prev,
      [currentGlobalIndex]: answer.trim() !== "" ? "answered" : "not_answered",
    }));
  };

  const handleMarkAndSave = () => {
    const currentGlobalIndex = getCurrentGlobalIndex();

    setQuestionStatuses((prev) => ({
      ...prev,
      [currentGlobalIndex]: "marked",
    }));

    handleNextQuestion();
  };

  const handleNextQuestion = () => {
    const currentQuestions = getCurrentQuestions();

    // Try to move to next question in current passage
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      return;
    }

    // Try to move to next passage in current section
    const currentSection = examData.sections[currentSectionIndex];
    if (currentPassageIndex < currentSection.passages.length - 1) {
      setCurrentPassageIndex(currentPassageIndex + 1);
      setCurrentQuestionIndex(0);
      return;
    }

    // Try to move to next section
    if (currentSectionIndex < examData.sections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      setCurrentPassageIndex(0);
      setCurrentQuestionIndex(0);
    }
  };

  const handlePreviousQuestion = () => {
    // Try to move to previous question in current passage
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      return;
    }

    // Try to move to previous passage in current section
    if (currentPassageIndex > 0) {
      const prevPassageIndex = currentPassageIndex - 1;
      const prevPassageQuestions =
        examData.sections[currentSectionIndex].passages[prevPassageIndex]
          .questions;
      setCurrentPassageIndex(prevPassageIndex);
      setCurrentQuestionIndex(prevPassageQuestions.length - 1);
      return;
    }

    // Try to move to previous section
    if (currentSectionIndex > 0) {
      const prevSectionIndex = currentSectionIndex - 1;
      const prevSection = examData.sections[prevSectionIndex];
      const lastPassageIndex = prevSection.passages.length - 1;
      const lastPassageQuestions =
        prevSection.passages[lastPassageIndex].questions;
      setCurrentSectionIndex(prevSectionIndex);
      setCurrentPassageIndex(lastPassageIndex);
      setCurrentQuestionIndex(lastPassageQuestions.length - 1);
    }
  };

  const handleQuestionNavigation = (
    sectionIndex,
    passageIndex,
    questionIndex
  ) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentPassageIndex(passageIndex);
    setCurrentQuestionIndex(questionIndex);
  };

  const handleSectionChange = (sectionIndex) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentPassageIndex(0);
    setCurrentQuestionIndex(0);
  };

  const handlePassageChange = (sectionIndex, passageIndex) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentPassageIndex(passageIndex);
    setCurrentQuestionIndex(0);
  };

  const getCurrentAnswer = () => {
    const currentGlobalIndex = getCurrentGlobalIndex();
    return userAnswers[currentGlobalIndex]?.answer || "";
  };

  const clearCurrentResponse = () => {
    const currentGlobalIndex = getCurrentGlobalIndex();
    const updatedAnswers = { ...userAnswers };
    updatedAnswers[currentGlobalIndex] = {
      type: getCurrentQuestion().question_type,
      answer: null,
    };
    setUserAnswers(updatedAnswers);
    setQuestionStatuses((prev) => ({
      ...prev,
      [currentGlobalIndex]: "not_answered",
    }));
  };

  // Helper function to get question status for display
  const getQuestionStatus = (sectionIndex, passageIndex, questionIndex) => {
    const allQuestions = getAllQuestions();
    const question = allQuestions.find(
      (q) =>
        q.sectionIndex === sectionIndex &&
        q.passageIndex === passageIndex &&
        q.questionIndex === questionIndex
    );

    if (!question) return "not_answered";
    return questionStatuses[question.globalIndex] || "not_answered";
  };

  // Get status counts for current section only
  const getCurrentSectionStatusCounts = () => {
    const currentSectionQuestions = getCurrentSectionQuestions();
    let answered = 0,
      marked = 0,
      notAnswered = 0;

    currentSectionQuestions.forEach((question) => {
      const status = getQuestionStatus(
        currentSectionIndex,
        question.passageIndex,
        question.questionIndex
      );
      if (status === "answered") answered++;
      else if (status === "marked") marked++;
      else notAnswered++;
    });

    return { answered, marked, notAnswered };
  };

  if (loading) {
    return (
      <div className="exam-result-page">
        <div className="page_header"></div>
        <div className="result-loading">
          <div className="loading-spinner"></div>
          <h3>Loading exam...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="exam-error">{error}</div>;
  }

  if (!examData) {
    return <div className="exam-error">No exam data found</div>;
  }

  const currentQuestion = getCurrentQuestion();
  const currentQuestions = getCurrentQuestions();
  const currentAnswer = getCurrentAnswer();
  const isTimeUp = timeLeft === 0;
  const currentSectionQuestions = getCurrentSectionQuestions();
  const currentSectionQuestionsCount = currentSectionQuestions.length;
  const sectionStatusCounts = getCurrentSectionStatusCounts();

  return (
    <div className="exam-page">
      <div className="exam-header">
        <h5>
          {examData.title} - {examData.package_name || "Test"}
        </h5>
        <div
          className={`timer ${
            timeLeft !== null && timeLeft < 300 ? "warning" : ""
          }`}
        >
          Time Left: {formatTime(timeLeft)}
        </div>
      </div>

      <div className="content">
        <div className="question-area">
          {/* Section Navigation - Horizontal Tabs */}
          <div className="section-navigation">
            {examData.sections.map((section, sIndex) => (
              <div key={section.id} className="section-tab-container">
                <button
                  className={`section-tab ${
                    sIndex === currentSectionIndex ? "active" : ""
                  }`}
                  onClick={() => handleSectionChange(sIndex)}
                >
                  {section.section_title}
                </button>
                {/* Passage Dropdown for each section */}
                {section.passages.length > 1 && (
                  <div className="passage-dropdown">
                    {section.passages.map((passage, pIndex) => (
                      <button
                        key={passage.id}
                        className={`passage-option ${
                          sIndex === currentSectionIndex &&
                          pIndex === currentPassageIndex
                            ? "active"
                            : ""
                        }`}
                        onClick={() => handlePassageChange(sIndex, pIndex)}
                      >
                        {passage.passage_title || `Passage ${pIndex + 1}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Content Tabs */}
          <div className="content-tabs">
            <button
              className={`tab-btn ${activeTab === "questions" ? "active" : ""}`}
              onClick={() => setActiveTab("questions")}
            >
              Questions
            </button>
            <button
              className={`tab-btn ${
                activeTab === "instructions" ? "active" : ""
              }`}
              onClick={() => setActiveTab("instructions")}
            >
              Instructions
            </button>
          </div>

          {activeTab === "instructions" ? (
            <div className="instructions-tab">
              <h6>Exam Instructions</h6>
              <div
                className="instructions-content p-3 border rounded bg-light"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    instructions || "No instructions provided."
                  ),
                }}
              />
            </div>
          ) : (
            <>
              {/* Current Context */}
              <div className="current-context">
                <h6>
                  Section:{" "}
                  {examData.sections[currentSectionIndex]?.section_title}
                  {examData.sections[currentSectionIndex]?.passages[
                    currentPassageIndex
                  ]?.passage_title &&
                    ` • Passage: ${examData.sections[currentSectionIndex].passages[currentPassageIndex].passage_title}`}
                </h6>
                <span>
                  Question {currentQuestionIndex + 1} of{" "}
                  {currentQuestions.length}
                  (Section:{" "}
                  {currentSectionQuestions.findIndex(
                    (q) =>
                      q.passageIndex === currentPassageIndex &&
                      q.questionIndex === currentQuestionIndex
                  ) + 1}{" "}
                  of {currentSectionQuestionsCount})
                </span>
              </div>

              <div className="my_question">
                {examData.sections[currentSectionIndex]?.passages[
                  currentPassageIndex
                ]?.passage_content && (
                  <div className="passage-content">
                    <div className="passage-card">
                      <h6>Passage:</h6>
                      <div
                        className="passage-text"
                        dangerouslySetInnerHTML={{
                          __html:
                            examData.sections[currentSectionIndex].passages[
                              currentPassageIndex
                            ].passage_content,
                        }}
                      />
                    </div>
                  </div>
                )}
                {/* Question Card */}
                {currentQuestion && (
                  <div className="question-card">
                    <div className="question-text">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            currentQuestion.question_text || ""
                          ),
                        }}
                      ></p>
                    </div>

                    {currentQuestion.question_type === "mcq" && (
                      <div className="options">
                        {currentQuestion.options &&
                          currentQuestion.options.map((option, idx) => {
                            const optionValue =
                              typeof option === "string"
                                ? option
                                : option.option_text;
                            const optionKey =
                              typeof option === "string"
                                ? option
                                : option.id || idx;

                            return (
                              <label key={optionKey} className="option">
                                <input
                                  type="radio"
                                  name="answer"
                                  checked={currentAnswer === optionValue}
                                  onChange={() =>
                                    handleOptionChange(optionValue)
                                  }
                                  disabled={isTimeUp}
                                />
                                <span className="option-text">
                                  {optionValue}
                                </span>
                              </label>
                            );
                          })}
                      </div>
                    )}

                    {currentQuestion.question_type === "true_false" && (
                      <div className="options">
                        <label className="option">
                          <input
                            type="radio"
                            name="answer"
                            checked={currentAnswer === "true"}
                            onChange={() => handleOptionChange("true")}
                            disabled={isTimeUp}
                          />
                          <span className="option-text">True</span>
                        </label>
                        <label className="option">
                          <input
                            type="radio"
                            name="answer"
                            checked={currentAnswer === "false"}
                            onChange={() => handleOptionChange("false")}
                            disabled={isTimeUp}
                          />
                          <span className="option-text">False</span>
                        </label>
                      </div>
                    )}

                    {currentQuestion.question_type === "short_answer" && (
                      <div className="text-answer">
                        <textarea
                          value={currentAnswer}
                          onChange={handleTextAnswerChange}
                          placeholder="Type your answer here..."
                          rows={5}
                          disabled={isTimeUp}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Question Actions */}
              <div className="question-actions">
                <div className="action-buttons-left">
                  <button
                    className="btn mark-save"
                    onClick={handleMarkAndSave}
                    disabled={isTimeUp}
                  >
                    Mark & Save
                  </button>
                  <button
                    className="btn clear"
                    onClick={clearCurrentResponse}
                    disabled={isTimeUp}
                  >
                    Clear Response
                  </button>
                </div>
                <div className="navigation-buttons">
                  <button
                    className="btn previous"
                    onClick={handlePreviousQuestion}
                    disabled={isTimeUp}
                  >
                    Previous
                  </button>
                  <button
                    className="btn save-next"
                    onClick={handleNextQuestion}
                    disabled={isTimeUp}
                  >
                    Save & Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Sidebar - Shows only current section questions */}

        <div
          className={`only_mobile_btn ${isSidebarVisible ? "hide" : ""}`}
          onClick={toggleSidebar}
        >
          {isSidebarVisible ? <FaChevronRight /> : <FaChevronLeft />}
        </div>
        <div className={`sidebar ${isSidebarVisible ? "active" : ""}`}>
          <div className="profile-info">
            <p>{username ? username : "Student"}</p>
          </div>

          <div className="right_box">
            {/* Current Section Info */}
            <div className="current-section-info">
              <h6>{examData.sections[currentSectionIndex]?.section_title}</h6>
              <p>Questions: {currentSectionQuestionsCount}</p>
            </div>

            <div className="status-box">
              <p>
                <div className="status green">
                  <span>{sectionStatusCounts.answered}</span>
                </div>
                Answered
              </p>
              <p>
                <div className="status yellow">
                  <span>{sectionStatusCounts.marked}</span>
                </div>
                Marked
              </p>
              <p>
                <div className="status red">
                  <span>{sectionStatusCounts.notAnswered}</span>
                </div>
                Not Answered
              </p>
            </div>

            <div className="question-navigator">
              <div className="section-questions">
                <div className="section-header">
                  {examData.sections[currentSectionIndex]?.section_title}
                  <span className="question-count">
                    ({currentSectionQuestionsCount} questions)
                  </span>
                </div>
                <div className="questions-grid">
                  {currentSectionQuestions.map((question, localIndex) => {
                    const status = getQuestionStatus(
                      currentSectionIndex,
                      question.passageIndex,
                      question.questionIndex
                    );
                    const isActive =
                      currentSectionIndex === question.sectionIndex &&
                      currentPassageIndex === question.passageIndex &&
                      currentQuestionIndex === question.questionIndex;

                    return (
                      <button
                        key={question.id}
                        className={`question-btn ${status} ${
                          isActive ? "active" : ""
                        }`}
                        onClick={() =>
                          handleQuestionNavigation(
                            question.sectionIndex,
                            question.passageIndex,
                            question.questionIndex
                          )
                        }
                        disabled={isTimeUp}
                        title={`Question ${localIndex + 1}`}
                      >
                        {localIndex + 1}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Section Navigation in Sidebar */}
            {/* <div className="section-sidebar-nav">
              <div className="section-nav-header">All Sections</div>
              <div className="section-buttons">
                {examData.sections.map((section, sIndex) => (
                  <button
                    key={section.id}
                    className={`section-nav-btn ${sIndex === currentSectionIndex ? 'active' : ''}`}
                    onClick={() => handleSectionChange(sIndex)}
                  >
                    {section.section_title}
                  </button>
                ))}
              </div>
            </div> */}

            <div className="side-buttons">
              <button
                className="btn submit-btn"
                onClick={handleSubmitTest}
                disabled={isSubmitting || isTimeUp}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Submitting...
                  </>
                ) : (
                  "Submit Test"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamPage;
